import forge from 'node-forge'

export function base64ToArrayBuffer(b64: string) {
    const str = window.atob(b64)
    const buf = new ArrayBuffer(str.length)
    const bufView = new Uint8Array(buf)
    for (let i = 0, strLen = str.length; i < strLen; i++) {
        bufView[i] = str.charCodeAt(i)
    }
    return buf
}

export function arrayBufferToBase64(buffer: ArrayBuffer) {
    let binary = ''
    const bytes = new Uint8Array(buffer)
    const len = bytes.byteLength
    for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i])
    }
    return window.btoa(binary)
}

function concatArrayBuffers(buffers: ArrayBuffer[]) {
    // 1. Calculate total length
    let totalLength = 0
    for (const buf of buffers) {
        totalLength += buf.byteLength
    }

    // 2. Create new Buffer and Uint8Array view
    const result = new Uint8Array(totalLength)

    // 3. Copy each buffer
    let offset = 0
    for (const buf of buffers) {
        result.set(new Uint8Array(buf), offset)
        offset += buf.byteLength
    }

    // 4. Return underlying ArrayBuffer
    return result.buffer
}

/**
 * Makes CryptoKey from public rsa key in PEM format
 */
export async function importVerifyRsaKey(publicKey: string) {
    // get the part of the PEM string between header and footer
    const pemHeader = '-----BEGIN PUBLIC KEY-----'
    const pemFooter = '-----END PUBLIC KEY-----'
    const headersRegex = new RegExp(`${pemHeader}|${pemFooter}|\\s`, 'g')
    const pemContents = publicKey.replace(headersRegex, '')
    // convert from a b64 binary string to an ArrayBuffer
    const binaryDer = base64ToArrayBuffer(pemContents)

    if (forge.pki.publicKeyFromPem(publicKey).n.bitLength() === 2048) {
        return await window.crypto.subtle.importKey(
            'spki',
            binaryDer,
            {
                name: 'RSASSA-PKCS1-v1_5',
                hash: 'SHA-256',
            },
            false,
            ['verify']
        )
    } else {
        // MAIN FLOW
        return await window.crypto.subtle.importKey(
            'spki',
            binaryDer,
            {
                name: 'RSA-PSS',
                hash: 'SHA-256',
            },
            false,
            ['verify']
        )
    }
}

export function trimNonJson(str: string) {
    return str.replace(/^[^{[]+|[^\]}]+$/g, '')
}

/**
 * Encrypt message by publicKey via RSA
 */
export async function encryptKeyByRSA(key: string, pk: string) {
    const publicKey = forge.pki.publicKeyFromPem(pk)

    let encrypted = ''

    if (publicKey.n.bitLength() === 2048) {
        encrypted = publicKey.encrypt(key, 'RSAES-PKCS1-V1_5')
    } else {
        // MAIN FLOW
        encrypted = publicKey.encrypt(key, 'RSA-OAEP', {
            md: forge.md.sha256.create(),
            mgf1: { md: forge.md.sha256.create() },
        })
    }

    return forge.util.encode64(encrypted)
}

export async function createAES() {
    return forge.random.getBytesSync(32)
}

export async function verifySignatureRsa(
    message: string,
    iv: string,
    signature: string,
    publicKey: CryptoKey,
    pk: string
) {
    const messageBuf = base64ToArrayBuffer(message)
    const ivBuf = base64ToArrayBuffer(iv)
    const signatureBuf = base64ToArrayBuffer(signature)

    if (forge.pki.publicKeyFromPem(pk).n.bitLength() === 2048) {
        return await window.crypto.subtle.verify(
            { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' },
            publicKey,
            signatureBuf,
            concatArrayBuffers([messageBuf, ivBuf])
        )
    } else {
        // MAIN FLOW
        return await window.crypto.subtle.verify(
            { name: 'RSA-PSS', saltLength: 32 },
            publicKey,
            signatureBuf,
            concatArrayBuffers([messageBuf, ivBuf])
        )
    }
}

export function decryptAES(encryptedDataBase64: string, ivBase64: string, aesKey: string) {
    // 1. Декодирование из Base64
    const encryptedBytes = forge.util.decode64(encryptedDataBase64)
    const iv = forge.util.decode64(ivBase64)

    // 2. Создание дешифратора
    const decipher = forge.cipher.createDecipher('AES-CBC', aesKey)

    // 3. Расшифровка
    decipher.start({ iv })
    decipher.update(forge.util.createBuffer(encryptedBytes))
    const success = decipher.finish()

    if (!success) {
        throw new Error('Decryption failed')
    }

    // 4. Возврат расшифрованного текста
    return decipher.output.toString()
}
